
import { Box, Grid, Button, DialogActions } from "@material-ui/core";
import { IdentityApplication } from "libs/resources/identity/applications/PublisherApplicationService";
import React, { useState, useContext, useEffect } from "react";
import { SnackNotificationContext } from "legacy-components/notifications/GenericSnackNotification";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import { Alert } from "@material-ui/lab";
import InfoIcon from "@material-ui/icons/Info";
import { ScopeData } from "../pages/application-summary-page/ResourceServerConfig";
import ApiService from "pages/home/apis/services/ApiService";

interface EditScopeConfigurationProps {
  application: IdentityApplication;
  selectedScope: ScopeData;
  onClose?: (wasUpdated?: boolean) => void;
}

/**
 * Renders an edit form for a specific scope where only the alwaysShowConsent field can be modified.
 *
 * @param {Object} props - The component props.
 * @param {IdentityApplication} props.application - The application object.
 * @param {ScopeData} props.selectedScope - The scope being edited.
 * @param {Function} props.onClose - Function to call when editing is done.
 * @returns {JSX.Element} - The rendered component.
 */
export const EditScopeConfiguration: React.FC<EditScopeConfigurationProps> = ({
  application,
  selectedScope,
  onClose
}) => {
  const identityEnvironment = useSelector(
    (state: any) => state?.common?.identityEnvironment
  );

  const notification = useContext(SnackNotificationContext);

  const initialAlwaysShowConsent = selectedScope.alwaysShowConsent || false;
  const initialConsentMessage = selectedScope.consentMessage?.trim() || "";

  const isDefaultScope = selectedScope.isDefault || false;

  const [formValues, setFormValues] = useState({
    name: selectedScope.scope,
    developerDocumentation: selectedScope.developerDocumentation || "",
    consentMessage: selectedScope.consentMessage || "",
    alwaysShowConsent: initialAlwaysShowConsent
  });

  const [valueChanged, setValueChanged] = useState(false);

  const consumerKey: string = (() => {
    const gatewayResponses = application.applicationGatewayResponses;
    if (!gatewayResponses || gatewayResponses.length === 0) {
      return "";
    }

    if (gatewayResponses[0]?.organisationName === identityEnvironment) {
      return gatewayResponses[0]?.consumerKey || "";
    } else {
      return gatewayResponses[1]?.consumerKey || "";
    }
  })();

  const [updateScopeState, { subscribe: updateScope }] = useAsyncAction(() =>
    ApiService.updateScope(consumerKey, formValues.name, formValues.consentMessage, formValues.developerDocumentation, formValues.alwaysShowConsent)
  );

  const updateAlwaysShowConsent = (newValue: boolean) => {
    setFormValues(prevValues => {
      const next = {
        ...prevValues,
        alwaysShowConsent: newValue
      };
      const hasChanged = (Boolean(next.alwaysShowConsent) !== Boolean(initialAlwaysShowConsent));
      setValueChanged(hasChanged);
      return next;
    });
  };

  const updateConsentMessage = (newMessage: string) => {
    setFormValues(prevValues => {
      const next = {
        ...prevValues,
        consentMessage: newMessage
      };
      const hasChanged = next.consentMessage !== initialConsentMessage;
      setValueChanged(hasChanged);
      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateAlwaysShowConsent(e.target.checked);
  };

  const handleUserConsentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateConsentMessage(e.target.value);
  }

  const handleSubmit = () => {
    // Only make the API call if the value has changed
    if (valueChanged && consumerKey) {
      updateScope();
    } else if (!consumerKey) {
      notification.show({
        id: "update-scope-error",
        message: <FormattedMessage defaultMessage="Missing consumer key." />,
        timeout: 4000
      });
    }
  };

  // Handle cancel click separately to prevent page reload
  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onClose) {
      onClose(false);
    }
  };

  useEffect(() => {
    if (!updateScopeState) {
      return;
    }

    if (updateScopeState.error) {
      const error = updateScopeState.errorValue as any;
      const status = error?.status || error?.response?.status;

      // Only show notification for non-403 and 500 errors
      if (status !== 403 && status < 499) {
        notification.show({
          id: "update-scope-error",
          message: <FormattedMessage defaultMessage="Unknown error occurred while updating the scope." />,
          timeout: 4000
        });
      }
    }

    if (updateScopeState.loaded) {
      notification.show({
        id: "update-scope-success",
        message: <FormattedMessage defaultMessage="Scope updated successfully." />,
        timeout: 4000
      });

      if (onClose) {
        onClose(true);
      }
    }
  }, [notification, updateScopeState, onClose]);

  return (
    <>
      <Box paddingLeft={3} paddingRight={3} paddingTop={2}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            {(!Boolean(initialConsentMessage) && isDefaultScope) ? (<Alert severity="info" variant="outlined" iconMapping={{ info: <InfoIcon color="primary" /> }}>
              You can edit the "Always Show Consent" and add "User Consent Message" setting for this scope.
            </Alert>) :
              (<Alert severity="info" variant="outlined" iconMapping={{ info: <InfoIcon color="primary" /> }}>
                You can only edit the "Always Show Consent" setting for this scope.
              </Alert>)}
          </Grid>

          <Grid item xs={12}>
            Scope Name <span className="required">*</span>
            <Box display="flex" flexDirection="row">
              <InputContainer
                value={formValues.name}
                disabled={true}
                required
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            User Consent Message <span className="required">*</span>
            <Box display="flex" flexDirection="row" style={{ gap: "14px" }}>
              <InputContainer
                inputStyle={{ height: "100px", padding: "12px 12px" }}
                rows={4}
                multiline={true}
                value={formValues.consentMessage}
                onChange={handleUserConsentChange}
                validate={{ allowSpace: true }}
                disabled={!isDefaultScope || Boolean(initialConsentMessage)}
                required
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            Developer Documentation <span className="required">*</span>
            <Box display="flex" flexDirection="row" style={{ gap: "14px" }}>
              <InputContainer
                inputStyle={{ height: "100px", padding: "12px 12px" }}
                rows={4}
                multiline={true}
                value={formValues.developerDocumentation}
                disabled={true}
                required
              />
            </Box>
            <Box display="flex" alignItems="center" mt={2}>
              <input
                type="checkbox"
                id="alwaysShowConsent"
                name="alwaysShowConsent"
                checked={formValues.alwaysShowConsent}
                onChange={handleChange}
                style={{ marginRight: "8px" }}
              />
              <label htmlFor="alwaysShowConsent">Always ask consent for this scope</label>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <DialogActions style={{ padding: "20px 35px" }}>
        <Button onClick={handleCancel} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Box ml={1} />
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={updateScopeState?.loading || !valueChanged}
          variant="contained"
        >
          Update
        </Button>
      </DialogActions>
    </>
  );
};